import React, { Fragment, useMemo, useState } from "react";
import { Container, Alert, Badge, Button, Spinner, Input } from "reactstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  UserX,
  Disc,
  Tool,
  Filter,
} from "react-feather";
import { Breadcrumbs } from "../../../AbstractElements";
import AfrService from "../../../Services/afr/AfrService";
import ValidationService from "../../../Services/validation/ValidationService";
import IncomeService from "../../../Services/income/IncomeService";
import { APP_PATHS } from "../../../utils/appPaths";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "../Validation/validation.scss";

const STATUS_META = {
  healthy: {
    label: "Clear",
    color: "success",
    Icon: CheckCircle,
    message: "No AFR distribution rows or orphan ISRCs detected.",
  },
  warning: {
    label: "Review",
    color: "warning",
    Icon: AlertTriangle,
    message: "Minor AFR items need review.",
  },
  degraded: {
    label: "Action needed",
    color: "danger",
    Icon: XCircle,
    message: "Unmatched distributions or orphan ISRCs require remediation.",
  },
  critical: {
    label: "Critical",
    color: "danger",
    Icon: XCircle,
    message: "Large AFR backlog detected. Prioritize fixes immediately.",
  },
};

const SEVERITY_ICON = {
  critical: XCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const SEVERITY_COLOR = {
  critical: "danger",
  error: "danger",
  warning: "warning",
  info: "info",
};

const SECTION_STATUS_COLOR = {
  pass: "success",
  fail: "danger",
  warn: "warning",
};

const formatMoney = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return `${num.toFixed(2)} EUR`;
};

const formatPeriod = (start, end) => {
  if (!start && !end) return "—";
  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "?");
  return `${fmt(start)} – ${fmt(end)}`;
};

const StatusBadge = ({ status }) => {
  const normalized = STATUS_META[status] ? status : "warning";
  const meta = STATUS_META[normalized];
  return (
    <Badge color={meta.color} className="val-badge">
      {meta.label}
    </Badge>
  );
};

const MetricPill = ({ metric }) => {
  const status = metric?.status || "pass";
  return (
    <div className={`val-metric val-metric--${status}`}>
      <p className="val-metric__value">{metric?.value ?? "—"}</p>
      <p className="val-metric__label">{metric?.label ?? ""}</p>
    </div>
  );
};

const AfrPage = () => {
  const [incomeFilter, setIncomeFilter] = useState("");
  const [fixingAction, setFixingAction] = useState(null);
  const queryClient = useQueryClient();

  const { data: incomesResponse } = useQuery({
    queryKey: ["incomeListForAfr"],
    queryFn: () => IncomeService.getAllIncomes(),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });

  const incomeOptions = incomesResponse?.data ?? [];

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["afrOverview", incomeFilter],
    queryFn: () =>
      AfrService.getOverview({
        incomeId: incomeFilter || undefined,
        isrcLimit: 100,
        incomeLimit: 100,
        orphanLimit: 100,
        refresh: false,
      }),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
  });

  const handleRefresh = async () => {
    try {
      const result = await queryClient.fetchQuery({
        queryKey: ["afrOverview", incomeFilter],
        queryFn: () =>
          AfrService.getOverview({
            incomeId: incomeFilter || undefined,
            isrcLimit: 100,
            incomeLimit: 100,
            orphanLimit: 100,
            refresh: true,
          }),
        staleTime: 0,
      });
      queryClient.setQueryData(["afrOverview", incomeFilter], result);
      toast.success("Server cache cleared — AFR data refreshed.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to refresh AFR data."
      );
    }
  };

  const statusMeta = STATUS_META[data?.status] || STATUS_META.warning;
  const StatusIcon = statusMeta.Icon;
  const summary = data?.summary ?? {};
  const issues = data?.issues ?? [];
  const sections = data?.sections ?? [];
  const byIncome = data?.by_income ?? [];
  const isrcWorklist = data?.isrc_worklist ?? [];
  const orphanIsrcs = data?.orphan_isrcs ?? [];
  const remediations = data?.remediations ?? [];

  const sortedIssues = useMemo(() => {
    const order = { critical: 0, error: 1, warning: 2, info: 3 };
    return [...issues].sort(
      (a, b) => (order[a.severity] ?? 9) - (order[b.severity] ?? 9)
    );
  }, [issues]);

  const applicableFixes = useMemo(() => {
    const fromIssues = sortedIssues
      .filter((i) => i.fix?.action)
      .map((i) => i.fix.action);
    return [...new Set(fromIssues)];
  }, [sortedIssues]);

  const fixMutation = useMutation({
    mutationFn: ({ action, dryRun }) => ValidationService.remediate(action, { dryRun }),
    onMutate: ({ action }) => setFixingAction(action),
    onSettled: () => setFixingAction(null),
    onSuccess: (result) => {
      toast.success(result.message || "Fix applied successfully.");
      queryClient.invalidateQueries({ queryKey: ["afrOverview"] });
      queryClient.invalidateQueries({ queryKey: ["validationOverview"] });
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Fix failed.";
      toast.error(msg);
    },
  });

  const runFix = (action, dryRun = false) => {
    fixMutation.mutate({ action, dryRun });
  };

  const distributionCounts = summary.distribution_counts ?? {};

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Artist For Review (AFR)" />
      <Container fluid className="rd-validation">
        <div className="val-toolbar">
          <div>
            <p className="val-deep-note mb-0">
              Full view of unmatched royalty distributions (Artist For Review), orphan ISRCs, and per-income breakdown.
            </p>
          </div>
          <div className="val-toolbar__actions">
            <div className="d-flex align-items-center gap-2">
              <Filter size={14} className="text-muted" />
              <Input
                type="select"
                bsSize="sm"
                value={incomeFilter}
                onChange={(e) => setIncomeFilter(e.target.value)}
                disabled={isFetching}
                style={{ minWidth: 220 }}
              >
                <option value="">All incomes</option>
                {incomeOptions.map((income) => (
                  <option key={income.id} value={income.id}>
                    #{income.id} — {income.title || "Untitled"}
                  </option>
                ))}
              </Input>
            </div>
            <Button
              color="primary"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              title="Clear server cache and reload AFR metrics"
            >
              {isFetching ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <RefreshCw size={14} className="me-1" />
              )}
              {data?.cached ? "Refresh cache" : "Refresh"}
            </Button>
          </div>
        </div>

        {incomeFilter && (
          <Alert color="info" className="py-2 px-3 mb-3">
            Showing AFR data filtered to income #{incomeFilter}. Clear the filter to see the global view.
          </Alert>
        )}

        {data?.cached && !isFetching && (
          <Alert color="light" className="py-2 px-3 mb-3 border">
            Showing cached results (up to 5 minutes old). Click <strong>Refresh cache</strong> to reload from the database and S3.
          </Alert>
        )}

        {isError && (
          <Alert color="danger">
            Failed to load AFR data: {error?.response?.data?.message || error?.message || "Unknown error"}
          </Alert>
        )}

        {isLoading && !data ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading AFR overview…</p>
          </div>
        ) : data ? (
          <>
            <div className="val-status-banner">
              <div className="val-status-banner__icon">
                <StatusIcon
                  size={28}
                  className={`text-${statusMeta.color}`}
                  aria-hidden
                />
              </div>
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h2 className="val-status-banner__title">Artist For Review</h2>
                  <StatusBadge status={data.status} />
                </div>
                <p className="val-status-banner__meta">
                  {statusMeta.message}
                  {data.timestamp && (
                    <> · Last run {new Date(data.timestamp).toLocaleString()}</>
                  )}
                  {typeof data.duration_ms === "number" && data.duration_ms > 0 && (
                    <> · {data.duration_ms < 1000
                      ? `${data.duration_ms}ms`
                      : `${(data.duration_ms / 1000).toFixed(1)}s`}</>
                  )}
                  {data.cached && <> · cached</>}
                </p>
              </div>
            </div>

            <div className="val-summary-grid">
              <div className="val-summary-card">
                <p className="val-summary-card__value text-danger">
                  {summary.total_records ?? 0}
                </p>
                <p className="val-summary-card__label">AFR rows</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value">
                  {summary.unique_isrcs ?? 0}
                </p>
                <p className="val-summary-card__label">Unique ISRCs</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value text-warning">
                  {formatMoney(summary.total_revenue)}
                </p>
                <p className="val-summary-card__label">Revenue at risk</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value text-danger">
                  {summary.orphan_isrcs ?? 0}
                </p>
                <p className="val-summary-card__label">Orphan ISRCs</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value">
                  {summary.incomes_affected ?? 0}
                </p>
                <p className="val-summary-card__label">Incomes affected</p>
              </div>
              <div className="val-summary-card">
                <p className="val-summary-card__value">
                  {(distributionCounts.title ?? 0) +
                    (distributionCounts.dsp ?? 0) +
                    (distributionCounts.country ?? 0) +
                    (distributionCounts.typesale ?? 0)}
                </p>
                <p className="val-summary-card__label">Total by type</p>
              </div>
            </div>

            {applicableFixes.length > 0 && (
              <article className="val-panel val-panel--full mb-3">
                <header className="val-panel__header">
                  <h3 className="val-panel__title">
                    <Tool size={14} className="me-1" />
                    Automated fixes
                  </h3>
                </header>
                <div className="val-panel__body p-3">
                  <div className="val-fix-grid">
                    {remediations
                      .filter((r) => applicableFixes.includes(r.action))
                      .map((rem) => (
                        <div className="val-fix-card" key={rem.action}>
                          <h4 className="val-fix-card__title">{rem.label}</h4>
                          <p className="val-fix-card__desc">{rem.description}</p>
                          <div className="val-fix-card__actions">
                            <Button
                              color="primary"
                              size="sm"
                              disabled={fixMutation.isPending}
                              onClick={() => runFix(rem.action, false)}
                            >
                              {fixingAction === rem.action ? (
                                <Spinner size="sm" className="me-1" />
                              ) : null}
                              Fix now
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </article>
            )}

            <div className="val-layout">
              <article className="val-panel">
                <header className="val-panel__header">
                  <h3 className="val-panel__title">
                    <UserX size={14} className="me-1" />
                    Issues requiring attention
                  </h3>
                  <Badge color="light">{sortedIssues.length}</Badge>
                </header>
                <div className="val-panel__body">
                  {sortedIssues.length === 0 ? (
                    <p className="val-empty">No AFR issues detected.</p>
                  ) : (
                    sortedIssues.map((issue, index) => {
                      const Icon = SEVERITY_ICON[issue.severity] || Info;
                      return (
                        <div className="val-issue" key={`${issue.title}-${index}`}>
                          <div className="val-issue__head">
                            <Icon
                              size={18}
                              className={`text-${SEVERITY_COLOR[issue.severity] || "secondary"} mt-1`}
                            />
                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <h4 className="val-issue__title">{issue.title}</h4>
                                <Badge color={SEVERITY_COLOR[issue.severity] || "secondary"} pill>
                                  {issue.severity}
                                </Badge>
                                {issue.category && (
                                  <Badge color="light">{issue.category}</Badge>
                                )}
                              </div>
                              <p className="val-issue__message">{issue.message}</p>
                              {issue.action && (
                                <p className="val-issue__action">
                                  <strong>Action:</strong> {issue.action}
                                </p>
                              )}
                              {issue.fix?.action && (
                                <div className="val-issue__fix mt-2">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    outline
                                    disabled={fixMutation.isPending}
                                    onClick={() => runFix(issue.fix.action, false)}
                                  >
                                    {fixingAction === issue.fix.action ? (
                                      <Spinner size="sm" className="me-1" />
                                    ) : null}
                                    {issue.fix.label || "Fix"}
                                  </Button>
                                </div>
                              )}
                              {issue.meta?.income_ids?.length > 0 && (
                                <ul className="val-link-list">
                                  {issue.meta.income_ids.map((id) => (
                                    <li key={id}>
                                      <Link to={APP_PATHS.incomeReport(id)}>Income #{id}</Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </article>

              <article className="val-panel">
                <header className="val-panel__header">
                  <h3 className="val-panel__title">
                    <Disc size={14} className="me-1" />
                    Distribution breakdown
                  </h3>
                </header>
                <div className="val-panel__body">
                  {sections.map((section) => (
                    <div className="val-section" key={section.id}>
                      <div className="val-section__head">
                        <div>
                          <h4 className="val-section__title">{section.title}</h4>
                          <p className="val-section__desc">{section.description}</p>
                        </div>
                        <Badge color={SECTION_STATUS_COLOR[section.status] || "secondary"}>
                          {section.status}
                        </Badge>
                      </div>
                      <div className="val-metrics">
                        {(section.metrics ?? []).map((metric) => (
                          <MetricPill key={`${section.id}-${metric.label}`} metric={metric} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              {byIncome.length > 0 && (
                <article className="val-panel val-panel--full">
                  <header className="val-panel__header">
                    <h3 className="val-panel__title">AFR by income report</h3>
                    <Badge color="danger">{byIncome.length}</Badge>
                  </header>
                  <div className="val-panel__body p-3">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Income</th>
                            <th>Period</th>
                            <th>Title</th>
                            <th>DSP</th>
                            <th>Country</th>
                            <th>Sale type</th>
                            <th>Total rows</th>
                            <th>Revenue</th>
                            <th>Streams</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byIncome.map((row) => (
                            <tr key={row.income_id}>
                              <td>
                                <Link to={APP_PATHS.incomeReport(row.income_id)}>
                                  #{row.income_id}
                                </Link>
                                {row.income_title && (
                                  <div className="small text-muted">{row.income_title}</div>
                                )}
                              </td>
                              <td className="small">
                                {formatPeriod(row.period_start, row.period_end)}
                              </td>
                              <td>{row.title ?? 0}</td>
                              <td>{row.dsp ?? 0}</td>
                              <td>{row.country ?? 0}</td>
                              <td>{row.typesale ?? 0}</td>
                              <td className="fw-semibold">{row.total_records ?? 0}</td>
                              <td>{formatMoney(row.total_revenue)}</td>
                              <td>{row.total_streams ?? 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </article>
              )}

              {isrcWorklist.length > 0 && (
                <article className="val-panel val-panel--full">
                  <header className="val-panel__header">
                    <h3 className="val-panel__title">ISRC worklist — fix priority by revenue</h3>
                    <Badge color="warning">{isrcWorklist.length}</Badge>
                  </header>
                  <div className="val-panel__body p-3">
                    <p className="val-deep-note mb-3">
                      Each ISRC below is linked to Artist For Review. Open the income report to assign the correct artist.
                    </p>
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>ISRC</th>
                            <th>Records</th>
                            <th>Revenue</th>
                            <th>Streams</th>
                            <th>Downloads</th>
                            <th>Types</th>
                            <th>Incomes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isrcWorklist.map((row) => (
                            <tr key={row.isrc}>
                              <td className="font-monospace small">{row.isrc}</td>
                              <td>{row.total_records ?? 0}</td>
                              <td className="text-danger fw-semibold">
                                {formatMoney(row.total_revenue)}
                              </td>
                              <td>{row.total_streams ?? 0}</td>
                              <td>{row.total_downloads ?? 0}</td>
                              <td>
                                {(row.distribution_types ?? []).map((t) => (
                                  <Badge key={t} color="light" className="me-1 text-uppercase">
                                    {t}
                                  </Badge>
                                ))}
                              </td>
                              <td>
                                <ul className="val-link-list mb-0">
                                  {(row.income_ids ?? []).slice(0, 5).map((id) => (
                                    <li key={id}>
                                      <Link to={APP_PATHS.incomeReport(id)}>#{id}</Link>
                                    </li>
                                  ))}
                                  {(row.income_ids ?? []).length > 5 && (
                                    <li className="text-muted">
                                      +{(row.income_ids ?? []).length - 5} more
                                    </li>
                                  )}
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </article>
              )}

              {orphanIsrcs.length > 0 && (
                <article className="val-panel val-panel--full">
                  <header className="val-panel__header">
                    <h3 className="val-panel__title">Orphan ISRCs — missing catalog assets</h3>
                    <Badge color="danger">{orphanIsrcs.length}</Badge>
                  </header>
                  <div className="val-panel__body p-3">
                    <div className="table-responsive">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>ISRC</th>
                            <th>Rows</th>
                            <th>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanIsrcs.map((row) => (
                            <tr key={row.isrc}>
                              <td className="font-monospace small">{row.isrc}</td>
                              <td>{row.row_count ?? 0}</td>
                              <td>{formatMoney(row.total_revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </article>
              )}
            </div>
          </>
        ) : null}
      </Container>
    </Fragment>
  );
};

export default AfrPage;
